import { emptyDraft, parseDraft, type OnboardingDraft } from './onboarding-state.js';

export type ConnectionStatus = 'connected' | 'number_selected' | 'activated';

/**
 * The WhatsApp connection is **server-owned**: the console reads it but never writes it. A
 * merchant cannot declare themselves connected — see ADR 0004.
 */
export interface ConsoleConnection {
  wabaId: string;
  phoneNumberId: string | null;
  displayPhoneNumber: string | null;
  verifiedName: string | null;
  status: ConnectionStatus;
}

export type BillingStatus = 'trialing' | 'trial_expired' | 'paid' | 'cancelled';

export interface ConsoleStoreSummary {
  id: string;
  name: string;
  phoneNumber: string;
  trialStartedAt: string | null;
  trialEndsAt: string | null;
  billingStatus: BillingStatus | null;
}

export interface OnboardingSnapshot {
  draft: OnboardingDraft;
  connection: ConsoleConnection | null;
  store: ConsoleStoreSummary | null;
}

export interface ConsolePhoneNumber {
  id: string;
  displayPhoneNumber: string;
  verifiedName: string | null;
  qualityRating: string | null;
  codeVerificationStatus: string | null;
}

export interface ActivationResult {
  store: ConsoleStoreSummary;
  connection: ConsoleConnection;
}

export class ConsoleApiError extends Error {
  constructor(message: string, readonly status: number, readonly detail: string | null) {
    super(message);
    this.name = 'ConsoleApiError';
  }
}

export interface ConsoleApi {
  getOnboarding(): Promise<{
    draft: unknown;
    connection: ConsoleConnection | null;
    store: ConsoleStoreSummary | null;
  }>;
  putOnboarding(draft: OnboardingDraft): Promise<void>;
  resetOnboarding(): Promise<OnboardingSnapshot>;
  exchangeWhatsappCode(input: {
    code: string;
    wabaId: string;
    phoneNumberId?: string;
  }): Promise<ConsoleConnection | null>;
  listWhatsappNumbers(): Promise<ConsolePhoneNumber[]>;
  selectWhatsappNumber(input: {
    phoneNumberId: string;
    displayPhoneNumber: string | null;
    verifiedName: string | null;
  }): Promise<ConsoleConnection | null>;
  activateWhatsapp(): Promise<ActivationResult>;
  disconnectWhatsapp(): Promise<void>;
}

export interface CreateConsoleApiInput {
  backendOrigin: string;
  getToken: () => Promise<string | null>;
  fetchImplementation?: typeof fetch;
}

export function createConsoleApi(input: CreateConsoleApiInput): ConsoleApi {
  const doFetch = input.fetchImplementation ?? fetch;

  /** Keeps browser network errors ("Failed to fetch") out of the screens. */
  async function requestOrFail(url: string, init: RequestInit): Promise<Response> {
    try {
      return await doFetch(url, init);
    } catch {
      throw new ConsoleApiError(
        'Sem conexão com a Marketside. Verifique sua internet e tente novamente.',
        0,
        null,
      );
    }
  }

  async function call(path: string, init: RequestInit = {}): Promise<unknown> {
    const token = await input.getToken();

    if (!token) {
      throw new ConsoleApiError('Sessão expirada. Entre novamente para continuar.', 401, null);
    }

    const headers = new Headers(init.headers);
    headers.set('authorization', `Bearer ${token}`);

    if (init.body) {
      headers.set('content-type', 'application/json');
    }

    const response = await requestOrFail(`${input.backendOrigin}/console${path}`, { ...init, headers });

    if (!response.ok) {
      throw new ConsoleApiError(
        messageForStatus(response.status),
        response.status,
        await readDetail(response),
      );
    }

    if (response.status === 204) {
      return null;
    }

    return response.json();
  }

  return {
    async getOnboarding() {
      const payload = (await call('/onboarding')) as {
        draft?: unknown;
        connection?: ConsoleConnection | null;
        store?: ConsoleStoreSummary | null;
      };

      return {
        draft: payload?.draft ?? {},
        connection: payload?.connection ?? null,
        store: payload?.store ?? null,
      };
    },

    async putOnboarding(draft) {
      await call('/onboarding', { method: 'PUT', body: JSON.stringify({ draft }) });
    },

    async resetOnboarding() {
      const payload = (await call('/onboarding', { method: 'DELETE' })) as {
        draft?: unknown;
        connection?: ConsoleConnection | null;
        store?: ConsoleStoreSummary | null;
      };

      return {
        draft: parseDraft(payload?.draft) ?? emptyDraft(),
        connection: payload?.connection ?? null,
        store: payload?.store ?? null,
      };
    },

    async exchangeWhatsappCode(body) {
      const payload = (await call('/whatsapp/exchange', {
        method: 'POST',
        body: JSON.stringify(body),
      })) as { connection?: ConsoleConnection | null };

      return payload?.connection ?? null;
    },

    async listWhatsappNumbers() {
      const payload = (await call('/whatsapp/numbers')) as { numbers?: ConsolePhoneNumber[] };

      return payload?.numbers ?? [];
    },

    async selectWhatsappNumber(body) {
      const payload = (await call('/whatsapp/select-number', {
        method: 'POST',
        body: JSON.stringify(body),
      })) as { connection?: ConsoleConnection | null };

      return payload?.connection ?? null;
    },

    async activateWhatsapp() {
      return (await call('/whatsapp/activate', { method: 'POST', body: '{}' })) as ActivationResult;
    },

    async disconnectWhatsapp() {
      await call('/whatsapp', { method: 'DELETE' });
    },
  };
}

function messageForStatus(status: number): string {
  if (status === 401) {
    return 'Sessão expirada. Entre novamente para continuar.';
  }

  if (status === 409) {
    return 'Ainda falta uma step antes de seguir.';
  }

  if (status === 502) {
    return 'A Meta não respondeu agora. Tente novamente em instantes.';
  }

  if (status === 503) {
    return 'A área da loja está indisponível no momento. Tente novamente em instantes.';
  }

  return 'Não foi possível concluir a ação. Tente novamente.';
}

async function readDetail(response: Response): Promise<string | null> {
  try {
    const payload = (await response.json()) as { detail?: unknown };

    if (typeof payload?.detail !== 'string') {
      return null;
    }

    return payload.detail;
  } catch {
    return null;
  }
}
